// Page-controls slot in the global AppShell header. Each owner page
// declares the controls it wants in the top bar (Period selector for
// dashboard surfaces, filter chips for Conversations); the layout
// always renders the Scope picker on the left and pipes the page's
// node in to the right of it.

import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from "react";

interface HeaderSlotCtx {
  right: ReactNode;
  setRight: (n: ReactNode, owner: object) => void;
  clearRight: (owner: object) => void;
}

const Ctx = createContext<HeaderSlotCtx>({
  right: null,
  setRight: () => {},
  clearRight: () => {},
});

export function HeaderSlotProvider({ children }: { children: ReactNode }) {
  const [right, setRightState] = useState<ReactNode>(null);
  // Track which page currently owns the slot. Last writer wins; cleanup
  // only clears when the cleanup-owner matches the current owner, so a
  // stale cleanup from an unmounting page can't wipe a brand-new
  // registration from the incoming page (the original race that left
  // Conversations filter chips on the Insights header).
  const ownerRef = useRef<object | null>(null);

  const setRight = (node: ReactNode, owner: object) => {
    ownerRef.current = owner;
    setRightState(node);
  };
  const clearRight = (owner: object) => {
    if (ownerRef.current === owner) {
      ownerRef.current = null;
      setRightState(null);
    }
  };

  return <Ctx.Provider value={{ right, setRight, clearRight }}>{children}</Ctx.Provider>;
}

export function useHeaderRightSlot(): ReactNode {
  return useContext(Ctx).right;
}

/**
 * Register a node to render in the header's right slot. Cleans up on
 * unmount via owner-token matching — a stale cleanup from a route the
 * user just left can't wipe the new page's controls.
 */
export function useRegisterHeaderRight(node: ReactNode, deps: ReadonlyArray<unknown>): void {
  const { setRight, clearRight } = useContext(Ctx);
  const ownerRef = useRef<object>({});
  useEffect(() => {
    setRight(node, ownerRef.current);
    return () => clearRight(ownerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}
